import type { RadioMessage, Sim } from '@atc/sim';

export const TRANSCRIPT_SCHEMA_VERSION = 1;
const DB_NAME = 'atc-transcripts';
const STORE = 'sessions';
const KEEP = 10;

export type TranscriptSpeaker = 'controller' | 'pilot' | 'system';
export type TranscriptChannel = 'typed' | 'speech' | 'radio' | 'system';
export type TranscriptOutcome = 'pending' | 'accepted' | 'rejected' | 'unrecognized' | 'clarification' | 'fallback';

export interface TranscriptEntry {
  id: string;
  simTimeS: number;
  wallTime: string;
  speaker: TranscriptSpeaker;
  channel: TranscriptChannel;
  text: string;
  callsign?: string;
  outcome?: TranscriptOutcome;
  details?: Record<string, unknown>;
}

export interface TranscriptSession {
  schemaVersion: number;
  id: string;
  startedAt: string;
  endedAt?: string;
  airport: string;
  seed?: number;
  settings: Record<string, unknown>;
  entries: TranscriptEntry[];
}

const sessionId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface TranscriptLedger {
  controller(text: string, channel: 'typed' | 'speech'): string;
  outcome(id: string, outcome: TranscriptOutcome, details?: Record<string, unknown>): void;
  radio(message: RadioMessage): void;
  system(text: string, details?: Record<string, unknown>): void;
  snapshot(): TranscriptSession;
  finish(): Promise<TranscriptSession>;
}

export function createTranscriptLedger(sim: Sim, metadata: { airport: string; seed?: number; settings: Record<string, unknown> }): TranscriptLedger {
  let serial = 0;
  const data: TranscriptSession = {
    schemaVersion: TRANSCRIPT_SCHEMA_VERSION,
    id: sessionId(),
    startedAt: new Date().toISOString(),
    airport: metadata.airport,
    ...(metadata.seed === undefined ? {} : { seed: metadata.seed }),
    settings: structuredClone(metadata.settings),
    entries: [],
  };
  const add = (entry: Omit<TranscriptEntry, 'id' | 'wallTime'> & { wallTime?: string }) => {
    const full: TranscriptEntry = { ...entry, id: `${data.id}:${++serial}`, wallTime: entry.wallTime ?? new Date().toISOString() };
    data.entries.push(full);
    return full.id;
  };
  return {
    controller(text, channel) { return add({ simTimeS: sim.simTimeS, speaker: 'controller', channel, text, outcome: 'pending' }); },
    outcome(id, outcome, details) {
      const entry = data.entries.find((e) => e.id === id);
      if (!entry) return;
      entry.outcome = outcome;
      if (details) entry.details = structuredClone(details);
    },
    radio(message) { add({ simTimeS: message.timeS, speaker: message.from === 'atc' ? 'controller' : 'pilot', channel: 'radio', text: message.text, ...(message.callsign ? { callsign: message.callsign } : {}), details: { kind: message.kind, radioId: message.id, ...(message.effect ? { effect: message.effect } : {}), ...(message.brain ? { brain: message.brain } : {}) } }); },
    system(text, details) { add({ simTimeS: sim.simTimeS, speaker: 'system', channel: 'system', text, ...(details ? { details } : {}) }); },
    snapshot() { return structuredClone(data); },
    async finish() {
      data.endedAt ??= new Date().toISOString();
      const copy = structuredClone(data);
      await saveTranscript(copy).catch(() => {});
      return copy;
    },
  };
}

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) return reject(new Error('IndexedDB unavailable'));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

export async function saveTranscript(session: TranscriptSession): Promise<void> {
  const db = await database();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(session);
    await transactionDone(tx);
    const all = await listTranscripts();
    for (const old of all.slice(KEEP)) await deleteTranscript(old.id);
  } finally { db.close(); }
}

export async function listTranscripts(): Promise<TranscriptSession[]> {
  const db = await database();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    const all = await requestResult<TranscriptSession[]>(req);
    return all.sort((a, b) => (b.endedAt ?? b.startedAt).localeCompare(a.endedAt ?? a.startedAt));
  } finally { db.close(); }
}

export async function deleteTranscript(id: string): Promise<void> {
  const db = await database();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    await transactionDone(tx);
  } finally { db.close(); }
}

const requestResult = <T>(req: IDBRequest<T>) => new Promise<T>((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });
const transactionDone = (tx: IDBTransaction) => new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error); });
const clock = (seconds: number) => `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor(seconds / 60) % 60).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

export function transcriptText(session: TranscriptSession): string {
  const header = `ATC transcript ${session.airport.toUpperCase()} — ${session.startedAt}`;
  return [header, ...session.entries.map((e) => `[${clock(e.simTimeS)}] ${e.speaker.toUpperCase()}${e.callsign ? ` ${e.callsign}` : ''}: ${e.text}${e.outcome && e.channel !== 'radio' ? ` [${e.outcome}]` : ''}`)].join('\n');
}

export function downloadTranscript(session: TranscriptSession, format: 'json' | 'text'): void {
  const text = format === 'json' ? JSON.stringify(session, null, 2) : transcriptText(session);
  const blob = new Blob([text], { type: format === 'json' ? 'application/json' : 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `atc-${session.airport}-${session.startedAt.replace(/[:.]/g, '-')}.${format === 'json' ? 'json' : 'txt'}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
